import {
    AttachFile,
    MailOutlineOutlined,
    PaymentsOutlined,
    PrintOutlined,
    TextSnippetOutlined,
    WorkspacePremiumOutlined
} from "@mui/icons-material";
import {useClientLocator} from "../../../client/ApiClientLocator";
import {SubmissionAttachmentDto, SubmissionAttachmentType, SubmissionDto, SubmissionStateType, TimestampAttachmentStatus} from "../../../client/submission/SubmissionApiClient";
import DocumentListItem from "./DocumentListItem";
import {SubmissionTranslKey} from "../SubmissionTranslKey";
import {PaymentTranslKey} from "../PaymentTranslKey";
import {useTranslation} from "react-i18next";
import {List} from "@mui/material";
import {CommonTranslKey} from "../../../common/CommonTranslKey";
import {useSnackbar} from "@dain/mui-components"
import {BlobUtils} from "../../../util/BlobUtils";
import Skeleton from "../../../common/skeleton/Skeleton";
import {useQuery} from "@tanstack/react-query";

export interface SubmissionDocumentsPanelProps {
    submission?: SubmissionDto;
}

export default function SubmissionDocumentsPanel(props: SubmissionDocumentsPanelProps) {
    const {submission} = props;

    const {submissionClient} = useClientLocator();
    const snackbar = useSnackbar();
    const {t} = useTranslation();

    const {data: attachments = [], isLoading} = useQuery({
        queryKey: ['submission.attachments', submission?.id ? submission.id : null],
        queryFn: async () => {
            if (!submission) {
                return Promise.reject(null);
            }
            return submissionClient.findSubmissionAttachments(submission.id);
        }
    });

    function downloadAttachmentDocument(attachment: SubmissionAttachmentDto) {
        submissionClient.downloadAttachmentDocument(attachment.submissionId, attachment.id)
            .then((documentBlob) => {
                BlobUtils.downloadBlob(documentBlob, attachment.fileName);
            })
            .catch((err) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err));
    }

    function downloadSubmittedDocument(submissionId: number, documentNumber: string | null) {
        submissionClient.downloadSubmittedDocument(submissionId)
            .then((documentBlob) => {
                const documentName = documentNumber ? `${documentNumber}` : `${submissionId}`;
                BlobUtils.downloadBlob(documentBlob, documentName + ".xml");
            })
            .catch((err) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err));
    }

    function downloadSubmittedPrintableDocument(submissionId: number, documentNumber: string | null) {
        submissionClient.downloadSubmittedPrintableDocument(submissionId)
            .then((documentBlob) => {
                const documentName = documentNumber ? `${documentNumber}` : `${submissionId}`;
                BlobUtils.downloadBlob(documentBlob, documentName + ".pdf");
            })
            .catch((err) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err));
    }

    function downloadNamedDocument(submissionId: number, documentNumber: string | null, label: SubmissionTranslKey,
                                   fallbackDownload: (submissionId: number) => Promise<Blob>, attachment?: SubmissionAttachmentDto) {
        const documentName = documentNumber ? `${documentNumber}` : `${submissionId}`;
        const fileName = `${documentName}_${t(label)}.pdf`;
        const download = attachment
            ? submissionClient.downloadAttachmentDocument(attachment.submissionId, attachment.id)
            : fallbackDownload(submissionId);
        download
            .then((documentBlob) => BlobUtils.downloadBlob(documentBlob, fileName))
            .catch((err) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err));
    }

    function downloadSubmissionPaymentInformationDocument(submissionId: number, documentNumber: string | null) {
        submissionClient.downloadSubmissionPaymentInformationDocument(submissionId)
            .then((documentBlob) => {
                const documentName = documentNumber ? `${documentNumber}` : `${submissionId}`;
                const fileName = `${documentName}_${t(PaymentTranslKey.PAYMENT_INFORMATION_FILE_NAME)}.pdf`;
                BlobUtils.downloadBlob(documentBlob, fileName);
            })
            .catch((err) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err));
    }

    function findAttachmentByType(attachments: SubmissionAttachmentDto[], attachmentType: SubmissionAttachmentType): SubmissionAttachmentDto | undefined {
        return attachments.find(attachment => attachment.attachmentType === attachmentType);
    }

    function notStampedBadge(attachment?: SubmissionAttachmentDto): string | undefined {
        const notStamped = attachment?.timestampStatus === TimestampAttachmentStatus.WAITING_FOR_TIMESTAMP
            || attachment?.timestampStatus === TimestampAttachmentStatus.TIMESTAMP_EXPIRED;
        return notStamped ? t(SubmissionTranslKey.ATTACHMENT_NOT_STAMPED) : undefined;
    }

    if (!submission || isLoading) {
        return (
            <Skeleton count={2}/>
        );
    }

    const certificationAttachment = findAttachmentByType(attachments, SubmissionAttachmentType.CERTIFICATION);
    const certificationAvailable = certificationAttachment || SubmissionStateType.PROCESSED === submission.currentSubmissionState.type.code;
    const coverLetterAvailable = findAttachmentByType(attachments, SubmissionAttachmentType.COVER_LETTER);
    const originalDocumentAvailable = findAttachmentByType(attachments, SubmissionAttachmentType.ORIGINAL);
    const submittedPrintableDocumentAvailable = findAttachmentByType(attachments, SubmissionAttachmentType.PRINTABLE_DOCUMENT);
    const paymentInformationAvailable = findAttachmentByType(attachments, SubmissionAttachmentType.PAYMENT_INFORMATION);

    const otherAttachments = attachments.filter(attachment => attachment.attachmentType === SubmissionAttachmentType.OTHER);

    return (
        <List component={"nav"}>
            {originalDocumentAvailable &&
                <DocumentListItem icon={TextSnippetOutlined}
                                  primaryText={t(SubmissionTranslKey.SUBMITTED_DOCUMENT)}
                                  onDownload={() => downloadSubmittedDocument(submission.id, submission.submissionNumber)}/>
            }
            {submittedPrintableDocumentAvailable &&
                <DocumentListItem icon={PrintOutlined}
                                  primaryText={t(SubmissionTranslKey.PRINTABLE_DOCUMENT)}
                                  onDownload={() => downloadSubmittedPrintableDocument(submission.id, submission.submissionNumber)}/>
            }
            {certificationAvailable &&
                <DocumentListItem icon={WorkspacePremiumOutlined}
                                  primaryText={t(SubmissionTranslKey.CERTIFICATION)}
                                  badge={notStampedBadge(certificationAttachment)}
                                  badgeTooltip={t(SubmissionTranslKey.ATTACHMENT_NOT_STAMPED_HINT)}
                                  onDownload={() => downloadNamedDocument(submission.id, submission.submissionNumber, SubmissionTranslKey.CERTIFICATION,
                                      (id) => submissionClient.downloadSubmissionCertificationDocument(id), certificationAttachment)}/>
            }
            {coverLetterAvailable &&
                <DocumentListItem icon={MailOutlineOutlined}
                                  primaryText={t(SubmissionTranslKey.COVER_LETTER)}
                                  badge={notStampedBadge(coverLetterAvailable)}
                                  badgeTooltip={t(SubmissionTranslKey.ATTACHMENT_NOT_STAMPED_HINT)}
                                  onDownload={() => downloadNamedDocument(submission.id, submission.submissionNumber, SubmissionTranslKey.COVER_LETTER,
                                      (id) => submissionClient.downloadSubmissionCoverLetterDocument(id), coverLetterAvailable)}/>
            }
            {paymentInformationAvailable &&
                <DocumentListItem icon={PaymentsOutlined}
                                  primaryText={t(PaymentTranslKey.PAYMENT_INFORMATION)}
                                  onDownload={() => downloadSubmissionPaymentInformationDocument(submission.id, submission.submissionNumber)}/>
            }

            {otherAttachments.map(attachment =>
                <DocumentListItem key={attachment.id}
                                  icon={AttachFile}
                                  primaryText={attachment.fileName}
                                  secondaryText={t(SubmissionTranslKey.SUBMITTED_DOCUMENT_ATTACHMENT)}
                                  badge={notStampedBadge(attachment)}
                                  badgeTooltip={t(SubmissionTranslKey.ATTACHMENT_NOT_STAMPED_HINT)}
                                  onDownload={() => downloadAttachmentDocument(attachment)}/>
            )}
        </List>
    );
}
